import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Download, Mail, FileText, Newspaper } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/use-toast";
import { formatDate } from "@/lib/utils";
import adminService from "@/services/adminService";
import leadService from "@/services/leadService";

const SOURCE_LABELS: Record<string, string> = {
  catalogo: "Catálogo",
  newsletter: "Newsletter",
};

const PAGE_LIMIT = 10;
/** Tamaño de página del export: trae la lista en pocas pasadas. */
const EXPORT_LIMIT = 100;

/**
 * Panel de leads capturados (HU-14): la lista de emails que entran por el
 * catálogo corporativo y por el newsletter, exportable para email marketing.
 */
const AdminLeads = () => {
  const { toast } = useToast();
  const [page, setPage] = useState(1);
  const [isExporting, setIsExporting] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-leads", page],
    queryFn: () => leadService.getLeads({ page, limit: PAGE_LIMIT }),
  });

  const leads = data?.leads ?? [];
  const totalPages = data?.pagination.pages ?? 0;

  /**
   * Exporta TODAS las páginas, no solo la visible: el listado es paginado y un
   * CSV parcial perdería capturas sin avisar.
   */
  const exportLeads = async () => {
    setIsExporting(true);
    try {
      const first = await leadService.getLeads({ page: 1, limit: EXPORT_LIMIT });
      const all = [...first.leads];

      for (let nextPage = 2; nextPage <= first.pagination.pages; nextPage += 1) {
        const next = await leadService.getLeads({
          page: nextPage,
          limit: EXPORT_LIMIT,
        });
        all.push(...next.leads);
      }

      adminService.exportToCSV(
        all.map((lead) => ({
          email: lead.email,
          origen: SOURCE_LABELS[lead.source] ?? lead.source,
          fecha: lead.createdAt,
        })),
        "leads"
      );

      toast({
        title: "Leads exportados",
        description: `Se exportaron ${all.length} leads.`,
      });
    } catch {
      toast({
        variant: "destructive",
        title: "No se pudieron exportar los leads",
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-playfair font-bold text-3xl text-brown-dark">
            Leads capturados
          </h1>
          <p className="text-brown-light">
            Emails del catálogo corporativo y del newsletter.
          </p>
        </div>
        <Button onClick={exportLeads} disabled={isExporting || leads.length === 0}>
          <Download className="h-4 w-4 mr-2" />
          Exportar CSV
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total leads</CardTitle>
            <Mail className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-brown-dark">
              {data?.pagination.total ?? 0}
            </div>
            <p className="text-xs text-muted-foreground">Contactos capturados</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Del catálogo</CardTitle>
            <FileText className="h-4 w-4 text-primary-dark" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-brown-dark">
              {leads.filter((l) => l.source === "catalogo").length}
            </div>
            <p className="text-xs text-muted-foreground">Descargas del catálogo</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Del newsletter</CardTitle>
            <Newspaper className="h-4 w-4 text-primary-dark" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-brown-dark">
              {leads.filter((l) => l.source === "newsletter").length}
            </div>
            <p className="text-xs text-muted-foreground">Suscripciones</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            {data?.pagination.total ?? 0} leads
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Origen</TableHead>
                  <TableHead>Fecha</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leads.map((lead) => (
                  <TableRow key={lead.id}>
                    <TableCell className="font-medium">{lead.email}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {SOURCE_LABELS[lead.source] ?? lead.source}
                      </Badge>
                    </TableCell>
                    <TableCell>{formatDate(lead.createdAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-end gap-2 mt-4">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-brown-light">
                Página {page} de {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((current) => current + 1)}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLeads;
